import { useEffect, useMemo, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import UserService from "../../services/UserService";
import { GetEmployeeResponseDto } from "../../services/dtos/response/UserResponseDto";
import TipService from "../../services/TipService";
import { getAzulResponseParams, AzulPaymentResponse } from "../../services/dtos/response/AzulResponseDto";
import { UpdateTipStatusDto } from "../../services/dtos/request/UpdateTipStatusDto";
import { TipStatus } from "../../services/dtos/TipStatus";

interface ConfirmationState {
  rating: number;
  googleLink: string | null | undefined;
  userId: string;
  branchId: string;
}

export function useConfirmationPageData() {
  const location = useLocation();
  const state = location.state as ConfirmationState | undefined;
  const params = new URLSearchParams(location.search);
  const userIdFromUrl = params.get("userId");
  const branchIdFromUrl = params.get("branchId");
  
  // Priorizar userId y branchId del state, si no están, usar los de la URL
  const userId = state?.userId || userIdFromUrl;
  const branchId = state?.branchId || branchIdFromUrl;
  
  const [employee, setEmployee] = useState<GetEmployeeResponseDto | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [tipStatusUpdated, setTipStatusUpdated] = useState(false);
  const userService = useMemo(() => new UserService(), []);
  const tipService = useMemo(() => new TipService(), []);
  const [error, setError] = useState<{ [key: string]: string }>({});
  const [azulResponse, setAzulResponse] = useState<AzulPaymentResponse | null>(null);
  const [isFromAzul, setIsFromAzul] = useState(false);
  
  // Capturar respuesta de Azul al cargar la página
  useEffect(() => {
    const responseParams = getAzulResponseParams();
    
    if (Object.keys(responseParams).length > 0) {
      setAzulResponse(responseParams);
      // Verificar si hay tipId en sessionStorage para confirmar que viene de Azul
      const tipIdFromStorage = sessionStorage.getItem('azulTipId');
      if (tipIdFromStorage) {
        setIsFromAzul(true);
      }
    }
  }, [tipService]);

  // Función helper para redirigir al googleLink si cumple las condiciones
  const redirectToGoogleLinkIfNeeded = useCallback(() => {
    if (employee?.countryIsoCode?.toUpperCase() === "DO") {
      return false;
    }

    const ratingFromStorage = sessionStorage.getItem('azulRating');
    const googleLinkFromStorage = sessionStorage.getItem('azulGoogleLink');
    
    if (ratingFromStorage && googleLinkFromStorage) {
      const rating = parseInt(ratingFromStorage, 10);
      if (rating >= 4 && googleLinkFromStorage.length > 0) { 
        // Esperar 4 segundos antes de redirigir al googleLink
        setTimeout(() => {
          if (googleLinkFromStorage.includes("https://")) {
            window.location.replace(googleLinkFromStorage);
          } else {
            window.location.replace(`https://${googleLinkFromStorage}`);
          }
        }, 4000);
        return true;
      }
    }
    return false;
  }, [employee?.countryIsoCode]);

  // Actualizar status del tip siempre que se vuelva de Azul (se ejecuta cuando employee está disponible)
  useEffect(() => {
    // Evitar ejecutar múltiples veces
    if (tipStatusUpdated) {
      return;
    }

    if (!employee) {
      return;
    }

    const responseParams = getAzulResponseParams();
    const hasAzulResponse = Object.keys(responseParams).length > 0;
    
    // Obtener tipId de sessionStorage (guardado antes de redirigir a Azul)
    const tipIdFromStorage = sessionStorage.getItem('azulTipId');
    
    // Solo actualizar si hay respuesta de Azul y hay tipId guardado
    if (hasAzulResponse && tipIdFromStorage) {
      const tipId = parseInt(tipIdFromStorage, 10);
      
      if (isNaN(tipId)) {
        console.error("[AZUL] tipId no es un número válido:", tipIdFromStorage);
        return;
      }
      
      setTipStatusUpdated(true);
      
      const responseMessageUpper = responseParams.ResponseMessage?.toUpperCase();
      
      // Determinar el status basado en la respuesta de Azul
      let status = TipStatus.APROBADA; // Por defecto
      if (responseMessageUpper === "APROBADA") {
        status = TipStatus.APROBADA;
      } else if (responseMessageUpper === "CANCELADA" || responseMessageUpper?.includes("CANCEL")) {
        status = TipStatus.CANCELADA;
      } else if (responseMessageUpper === "DECLINADA" || responseMessageUpper === "DENEGADA" || responseMessageUpper?.includes("DECLIN") || responseMessageUpper?.includes("DENEG")) {
        status = TipStatus.DECLINADA;
      }
      
      const updateData = new UpdateTipStatusDto();
      updateData.TipId = tipId;
      updateData.Status = status;
      
      // Incluir AzulOrderId si está disponible
      if (responseParams.AzulOrderId) {
        updateData.AzulOrderId = responseParams.AzulOrderId;
      }
      
      if (employee?.countryIsoCode) {
        updateData.CountryCode = employee.countryIsoCode.toUpperCase();
      }
      
      tipService.updateStatusTipInProcess(
        updateData,
        () => {
          // Después de actualizar el status exitosamente, verificar si hay que redirigir al googleLink
          redirectToGoogleLinkIfNeeded();
        },
        (errorMessage) => {
          console.error("[AZUL] Error al actualizar status:", errorMessage);
        }
      );
    }
  }, [tipService, employee, tipStatusUpdated, azulResponse, redirectToGoogleLinkIfNeeded]);

  // Redirigir a Google si viene del state (países que no son Dominicana)
  useEffect(() => {
    if (!employee) return;
    if (employee.countryIsoCode?.toUpperCase() === "DO") return;

    if (state && state.rating >= 4 && state?.googleLink && state?.googleLink.length > 0) {
      // Redirigir inmediatamente para países que no son Dominicana (no vienen de Azul)
      const googleLink = state?.googleLink;
      if (googleLink) {
        if (googleLink.includes("https://")) {
          window.location.replace(googleLink);
        } else {
          window.location.replace(`https://${googleLink}`);
        }
      }
    }
  }, [state, employee]);

  // Obtener employee
  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    const fetchEmployee = async () => {
      if (!userId) return;
      
      setIsLoading(true);

      try {
        await userService.getEmployee(
          {
            userid: +userId,
            branchid: +branchId!,
          },
          (data) => {
            setEmployee(data);
          },
          (error) => {
            setError({ load: error });
          }
        );
      } finally {
        if (!signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    if (userId && branchId) {
      fetchEmployee();
    }

    return () => {
      controller.abort();
    };
  }, [userService, userId, branchId]);

  return {
    employee,
    isLoading,
    error,
    isFromAzul,
    state
  };
}

